import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Drawer, Button, AgGridTable, Pagination } from 'components/common';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FiSearch, FiDownload } from 'react-icons/fi';
import ProjectStatusRenderer from 'components/app/crm/project/component/ProjectStatusRenderer';
import JobEntryCategoryBadge from './JobEntryCategoryBadge';
import apiAxios from 'helpers/apiAxios';

const fmtW = (n) => `${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 1 })}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  const [date] = String(iso).split('T');
  const [y, m, d] = date.split('-');
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d}-${MONTHS[parseInt(m, 10) - 1]}-${y}`;
};

const PER_PAGE_OPTIONS = [25, 50, 100, 200];

// Slide-over showing the detailed records behind any chart segment as a
// server-side AG Grid (search + sort + pagination + Excel export). extraParams
// narrows baseParams (e.g. { region }, { country }, { category }, period dates).
const JobEntryDrillDownPanel = ({ open, title, baseParams, extraParams, onClose }) => {
  const [rows, setRows]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [perPage, setPerPage]     = useState(50);
  const [search, setSearch]       = useState('');
  const [sort, setSort]           = useState({ by: 'job_entry_date', dir: 'DESC' });
  const [loading, setLoading]     = useState(false);
  const [exporting, setExporting] = useState(false);
  const searchTimer = useRef(null);

  const queryParams = useMemo(() => ({
    ...baseParams, ...extraParams,
    q: search || undefined, sort_by: sort.by, sort_dir: sort.dir,
  }), [baseParams, extraParams, search, sort]);

  const fetchRows = useCallback((p, pp) => {
    setLoading(true);
    apiAxios.get('/crm/dashboard/job_entries/get_detailed_data', {
      params: { ...queryParams, page: p, per_page: pp },
    })
      .then(res => {
        setRows(res.data?.rows || []);
        setTotal(res.data?.total_rows || 0);
      })
      .catch(() => { setRows([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [queryParams]);

  // Refetch from page 1 whenever the panel opens or query (search/sort/scope) changes.
  useEffect(() => {
    if (!open) return;
    setPage(1);
    fetchRows(1, perPage);
  // perPage changes are fetched by the page-size handler; depending on it here would double-fetch
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fetchRows]);

  const handleSearchInput = (value) => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearch(value), 400);
  };

  const handleSortChanged = ({ colId, sort: dir }) => {
    if (!colId || !dir) return;
    setSort({ by: colId, dir: dir === 'asc' ? 'ASC' : 'DESC' });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await apiAxios.get('/crm/dashboard/job_entries/export_detailed_data', {
        params: queryParams, responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `job-entries-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      window.alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const columnDefs = useMemo(() => ([
    {
      headerName: 'Job #', field: 'j_number', width: 130, minWidth: 110, flex: 0, pinned: 'left',
      cellRenderer: (p) => (
        p.data?.project_id
          ? <a href={`/crm/projects/${p.data.project_id}`} target="_blank" rel="noreferrer"
              className="tw-font-mono tw-font-bold tw-text-emerald-600 hover:tw-underline">{p.value || '—'}</a>
          : <span className="tw-font-mono tw-font-bold tw-text-gray-700">{p.value || '—'}</span>
      ),
    },
    {
      headerName: 'Quote #', field: 'q_number', width: 130, minWidth: 110, flex: 0,
      cellRenderer: (p) => (
        p.value
          ? <span className="tw-font-mono tw-font-semibold tw-text-yellow-600">{p.value}</span>
          : <span className="tw-text-gray-300">—</span>
      ),
    },
    {
      headerName: 'Category', field: 'category', minWidth: 140,
      cellRenderer: (p) => <JobEntryCategoryBadge value={p.value} />,
    },
    {
      headerName: 'Status', field: 'pipeline_status', minWidth: 150, sortable: false,
      cellRenderer: (p) => <ProjectStatusRenderer value={p.value} data={p.data} />,
    },
    { headerName: 'Building Status', field: 'building_status', minWidth: 160, tooltipField: 'building_status', valueFormatter: (p) => p.value || '—' },
    { headerName: 'Remark', field: 'remark', minWidth: 200, tooltipField: 'remark', valueFormatter: (p) => p.value || '—' },
    {
      headerName: 'Buildings', field: 'building_count', minWidth: 90, type: 'numericColumn',
      tooltipField: 'building_names', cellClass: 'tw-tabular-nums tw-text-center',
      cellRenderer: (p) => (
        <span title={p.data?.building_names} className="tw-cursor-help">
          {p.value} {Number(p.value) === 1 ? 'bldg' : 'bldgs'}
        </span>
      ),
    },
    {
      headerName: 'Building Names', field: 'building_names', minWidth: 160, sortable: false,
      tooltipField: 'building_names', valueFormatter: (p) => p.value || '—',
    },
    { headerName: 'Project', field: 'project_name', minWidth: 200, tooltipField: 'project_name',
      cellRenderer: (p) => (
        p.data?.project_id
          ? <Link to={`/crm/projects/${p.data.project_id}`} className="tw-text-blue-600 hover:tw-underline">{p.value || '—'}</Link>
          : <span className="tw-text-gray-700">{p.value || '—'}</span>
      ),
    },
    { headerName: 'Customer', field: 'customer_name', minWidth: 180, valueFormatter: (p) => p.value || '—' },
    { headerName: 'CSE Order Entry', field: 'cse_order_entry', minWidth: 150, valueFormatter: (p) => p.value || '—' },
    { headerName: 'Weight (M.T)', field: 'weight', minWidth: 120, valueFormatter: (p) => fmtW(p.value), cellClass: 'tw-tabular-nums tw-text-right' },
    { headerName: 'BOM (M.T)', field: 'bom_weight', minWidth: 110, valueFormatter: (p) => fmtW(p.value), cellClass: 'tw-tabular-nums tw-text-right' },
    { headerName: 'Sales Incharge', field: 'sales_incharge', minWidth: 130, valueFormatter: (p) => p.value || '—' },
    { headerName: 'Sales Unit', field: 'unit_name', minWidth: 130, valueFormatter: (p) => p.value || '—' },
    { headerName: 'Type of Sales', field: 'type_of_sales', minWidth: 120, valueFormatter: (p) => p.value || '—' },
    { headerName: 'Region', field: 'region', minWidth: 120, valueFormatter: (p) => p.value || '—' },
    { headerName: 'Country', field: 'country', minWidth: 130, valueFormatter: (p) => p.value || '—' },
    { headerName: 'Province', field: 'province', minWidth: 140, valueFormatter: (p) => p.value || '—' },
    { headerName: 'Job Entry Date', field: 'job_entry_date', minWidth: 130, valueFormatter: (p) => fmtDate(p.value) },
  ]), []);

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const drawerTitle = (
    <div className="tw-flex tw-items-center tw-justify-between tw-w-full tw-gap-3 tw-flex-wrap tw-pr-2">
      <div className="tw-min-w-0">
        <p className="tw-text-sm tw-font-bold tw-text-gray-900 tw-mb-0 tw-truncate">{title}</p>
        <p className="tw-text-xs tw-text-gray-400 tw-font-normal tw-mb-0">{total.toLocaleString()} job entries</p>
      </div>
      <div className="tw-flex tw-items-center tw-gap-2 tw-ml-auto">
        <div className="tw-relative">
          <FiSearch className="tw-absolute tw-left-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400" size={13} />
          <input
            type="text" placeholder="Search J# / Q# / project / customer…"
            defaultValue={search}
            onChange={e => handleSearchInput(e.target.value)}
            className="tw-pl-8 tw-pr-3 tw-py-1.5 tw-text-xs tw-w-56 tw-font-normal tw-border tw-border-gray-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400"
          />
        </div>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={FiDownload}
          loading={exporting}
          disabled={total === 0}
          onClick={handleExport}
          className="tw-text-emerald-700 hover:tw-bg-emerald-50 hover:tw-border-emerald-300"
        >
          Export Excel
        </Button>
      </div>
    </div>
  );

  const footer = (
    <div className="tw-w-full">
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalResults={total}
        perPage={perPage}
        onPageChange={(p) => { setPage(p); fetchRows(p, perPage); }}
        onPageSizeChange={(n) => { setPerPage(n); setPage(1); fetchRows(1, n); }}
        pageSizeOptions={PER_PAGE_OPTIONS}
        disabled={loading}
      />
    </div>
  );

  return (
    <Drawer
      show={open}
      onHide={onClose}
      title={drawerTitle}
      footer={footer}
      width="min(1000px, 100vw)"
      minWidth="340px"
    >
      <AgGridTable
        rowData={rows}
        columnDefs={columnDefs}
        loading={loading}
        pagination={false}
        filter={false}
        sort
        rowHeight={38}
        height="max(320px, calc(100dvh - 175px))"
        onSortChanged={handleSortChanged}
        enableCellTextSelection
      />
    </Drawer>
  );
};

JobEntryDrillDownPanel.propTypes = {
  open: PropTypes.bool, title: PropTypes.string,
  baseParams: PropTypes.object, extraParams: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};
JobEntryDrillDownPanel.defaultProps = { open: false, title: '', baseParams: {}, extraParams: {} };

export default JobEntryDrillDownPanel;
